import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import type { HandleSuggestion } from '../../lib/api';
import { useT } from '../../i18n';

/** A server suggestion with the token that lets a create route take it. */
export type HandleValue = HandleSuggestion;

interface HandlePickerProps {
  /**
   * Fetches suggestions. `gendered` is false for the first one and true for
   * 🎲, so queen/raja-style words appear only when someone shuffles (D147).
   */
  suggest: (options: { gendered: boolean }) => Promise<HandleSuggestion[]>;
  value: HandleValue | null;
  onChange: (value: HandleValue) => void;
  /** Defaults to "Handle"; the join page says "You'll be" (D153). */
  label?: string;
}

// How many recent handles 🎲 won't show again.
const RECENT = 5;
// Suggestions' tokens last 30 minutes (#69): a batch older than this is
// thrown away rather than offered.
export const STALE_BATCH_MS = 25 * 60_000;

// A fun handle (D147): shown large with a 🎲 for the next suggestion. Nobody
// types a handle, roots and the system root included (#69); the server only
// takes one it suggested.
export function HandlePicker({
  suggest,
  value,
  onChange,
  label,
}: HandlePickerProps) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const queue = useRef<HandleSuggestion[]>([]);
  const fetchedAt = useRef(0);
  const recent = useRef<string[]>([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const pick = useCallback((next: HandleSuggestion) => {
    recent.current = [
      ...recent.current.filter((h) => h !== next.handle),
      next.handle,
    ].slice(-RECENT);
    onChangeRef.current({ handle: next.handle, token: next.token });
  }, []);

  const shuffle = useCallback(
    async (gendered: boolean) => {
      const fresh = (list: HandleSuggestion[]) =>
        list.filter((s) => !recent.current.includes(s.handle));
      if (Date.now() - fetchedAt.current > STALE_BATCH_MS) queue.current = [];
      queue.current = fresh(queue.current);
      if (queue.current.length === 0) {
        setLoading(true);
        setFailed(false);
        try {
          const got = await suggest({ gendered });
          fetchedAt.current = Date.now();
          // Every one shown lately: still better than nothing.
          queue.current = fresh(got).length > 0 ? fresh(got) : got;
          // The rest of a plain batch isn't kept, so 🎲 asks again with
          // gendered words allowed.
          if (!gendered) queue.current = queue.current.slice(0, 1);
        } catch {
          queue.current = [];
        } finally {
          setLoading(false);
        }
      }
      const next = queue.current.shift();
      if (next) pick(next);
      else setFailed(true);
    },
    [suggest, pick]
  );

  // A new name means new suggestions; an empty value asks for a fresh one.
  // The page empties the value when the server refused a lapsed token, so
  // the rest of that batch (signed at the same time) goes too (#69).
  useEffect(() => {
    queue.current = [];
  }, [suggest]);
  useEffect(() => {
    if (value === null) {
      queue.current = [];
      void shuffle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === null, suggest]);

  return (
    <Box
      bg="surface.subtle"
      borderRadius="card"
      borderWidth="1px"
      borderColor="line"
      p={4}
    >
      <Text fontSize="13px" fontWeight={700} color="muted" mb={1}>
        {label ?? t('account.handle.label')}
      </Text>
      <HStack justify="space-between" spacing={3}>
        <Box minW={0} flex="1">
          {value?.handle ? (
            <Text
              data-testid="handle-shown"
              fontFamily="heading"
              fontWeight={800}
              fontSize="24px"
              lineHeight="30px"
              wordBreak="break-all"
            >
              @{value.handle}
            </Text>
          ) : (
            <HStack color="muted" fontSize="15px">
              {loading && <Spinner size="sm" />}
              <Text>
                {failed
                  ? t('account.handle.failed')
                  : t('account.handle.finding')}
              </Text>
            </HStack>
          )}
        </Box>
        <IconButton
          aria-label={t('account.handle.shuffle')}
          title={t('account.handle.shuffle')}
          icon={
            <Text as="span" fontSize="24px" aria-hidden="true">
              🎲
            </Text>
          }
          size="lg"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          bg="surface"
          borderRadius="full"
          isLoading={loading}
          onClick={() => void shuffle(true)}
        />
      </HStack>
      <Text fontSize="13px" color="muted" mt={2}>
        {t('account.handle.forever')}
      </Text>
    </Box>
  );
}
